import { CheckCircle2, Loader2, X, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import React from "react";
import { getWindowJobsiteConfig } from "../../types/runtime-config";

interface ClaimTokenPayload {
  purpose: string;
  businessId: string;
  email?: string;
}

/** Decode JWT payload without verification (client-side only — server verifies). */
const decodeClaimToken = (token: string): ClaimTokenPayload | null => {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      return null;
    }
    const payload = JSON.parse(atob(parts[1]));
    if (payload.purpose !== "business_claim") {
      return null;
    }
    return payload as ClaimTokenPayload;
  } catch {
    return null;
  }
};

type FormState = "idle" | "submitting" | "success" | "error";

/**
 * A banner + modal that allows prospects to claim their business from an
 * outreach link. Only renders when `?claim=<token>` is present in the URL.
 *
 * The banner appears at the top of the jobsite. Clicking the CTA opens an
 * inline signup modal. On success, the user is redirected to the dashboard.
 */
const ClaimBusinessBanner = () => {
  const [claimToken, setClaimToken] = React.useState<string | null>(null),
    [dismissed, setDismissed] = React.useState(false),
    [showModal, setShowModal] = React.useState(false),
    [formState, setFormState] = React.useState<FormState>("idle"),
    [errorMessage, setErrorMessage] = React.useState(""),
    [formData, setFormData] = React.useState({ email: "", password: "" });

  // Read claim token from URL on mount
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search),
      token = params.get("claim");
    if (!token) {
      return;
    }

    const payload = decodeClaimToken(token);
    if (!payload) {
      return;
    }

    setClaimToken(token);
    if (payload.email) {
      setFormData((prev) => ({ ...prev, email: payload.email! }));
    }
  }, []);

  // Don't render if no token or dismissed
  if (!claimToken || dismissed) {
    return null;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    },

    // Bounce to the dashboard's /claim page (the single OAuth-registered origin)
    // Via the API redirect — Google Identity Services can't run on per-jobsite
    // Subdomains. The API knows the dashboard URL; we only have apiUrl here.
    handleGoogleClaim = () => {
      const config = getWindowJobsiteConfig(),
        apiUrl = config?.apiUrl;
      if (!apiUrl || !claimToken) {
        setFormState("error");
        setErrorMessage("Sign-in is unavailable right now. Please use email instead.");
        return;
      }
      window.location.href = `${apiUrl}/api/auth/claim/start?claim=${encodeURIComponent(claimToken)}`;
    },

    handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const form = e.currentTarget;
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      setFormState("submitting");
      setErrorMessage("");

      try {
        const config = getWindowJobsiteConfig(),
          apiUrl = config?.apiUrl;
        if (!apiUrl) {
          throw new Error("API URL not configured");
        }

        const res = await fetch(`${apiUrl}/api/auth/claim`, {
            body: JSON.stringify({
              claimToken,
              email: formData.email,
              password: formData.password,
            }),
            headers: { "Content-Type": "application/json" },
            method: "POST",
          }),

          data = await res.json();

        if (!res.ok) {
          setFormState("error");
          setErrorMessage(data.error || "Something went wrong. Please try again.");
          return;
        }

        setFormState("success");

        // Redirect to dashboard with auth token after a brief success message
        const { token, dashboardUrl, businessId, siteId } = data;
        setTimeout(() => {
          const url = new URL(dashboardUrl);
          url.searchParams.set("authToken", token);
          if (businessId) {
            url.searchParams.set("businessId", businessId);
          }
          if (siteId) {
            url.searchParams.set("siteId", siteId);
          }
          window.location.href = url.toString();
        }, 1500);
      } catch {
        setFormState("error");
        setErrorMessage("Something went wrong. Please try again.");
      }
    };

  return (
    <>
      {/* Fixed banner at top */}
      <div className="jobsite:fixed jobsite:top-0 jobsite:left-0 jobsite:right-0 jobsite:z-[60] jobsite:bg-primary jobsite:text-primary-foreground" data-testid="claim-banner">
        <div className="jobsite:container jobsite:mx-auto jobsite:px-4">
          <div className="jobsite:flex jobsite:items-center jobsite:justify-between jobsite:gap-4 jobsite:py-3">
            <div className="jobsite:min-w-0">
              <p className="jobsite:text-sm jobsite:font-semibold jobsite:sm:text-base">
                Claim your free, AI-powered Jobsite that works for your business while you sleep.
              </p>
            </div>
            <div className="jobsite:flex jobsite:shrink-0 jobsite:items-center jobsite:gap-2">
              <Button
                variant="secondary"
                size="sm"
                className="jobsite:font-semibold"
                data-testid="claim-open"
                onClick={() => {
                  setShowModal(true);
                }}
              >
                Claim it
              </Button>
              <button
                type="button"
                data-testid="claim-dismiss"
                onClick={() => {
                  setDismissed(true);
                }}
                className="jobsite:p-1 jobsite:opacity-70 jobsite:transition-opacity jobsite:hover:opacity-100"
                aria-label="Dismiss"
              >
                <X className="jobsite:h-4 jobsite:w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Spacer to push page content below the fixed banner */}
      <div className="jobsite:h-12" />

      {/* Claim modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="jobsite:sm:max-w-md" data-testid="claim-dialog">
          <DialogHeader>
            <DialogTitle className="jobsite:font-heading jobsite:text-2xl">
              Claim Your Website
            </DialogTitle>
            <DialogDescription>
              Create your account to manage this website, track leads, and grow your business.
            </DialogDescription>
          </DialogHeader>

          {formState === "success" ? (
            <div className="jobsite:flex jobsite:flex-col jobsite:items-center jobsite:gap-3 jobsite:py-6 jobsite:text-center" data-testid="claim-success">
              <CheckCircle2 className="jobsite:h-12 jobsite:w-12 jobsite:text-emerald-500" />
              <p className="jobsite:text-lg jobsite:font-semibold jobsite:text-foreground">
                You're in!
              </p>
              <p className="jobsite:text-sm jobsite:text-muted-foreground">
                Taking you to your dashboard...
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="jobsite:space-y-4">
              {/* Self-contained colors — NOT the themeable `outline` variant.
                  That variant's hover resolves to the jobsite's `background` +
                  `accent-foreground` tokens, which on a light theme go
                  white-on-white. A Google sign-in button must stay legible on
                  every jobsite theme, so we pin Google's standard light-button
                  palette (white bg, dark text, light-gray hover). */}
              <button
                type="button"
                data-testid="claim-google"
                onClick={handleGoogleClaim}
                disabled={formState === "submitting"}
                style={{ borderRadius: "var(--jobsite-radius)" }}
                className="jobsite:flex jobsite:h-12 jobsite:w-full jobsite:items-center jobsite:justify-center jobsite:gap-2 jobsite:border jobsite:border-neutral-300 jobsite:bg-white jobsite:text-base jobsite:font-semibold jobsite:text-neutral-800 jobsite:shadow-xs jobsite:transition-colors jobsite:hover:bg-neutral-50 jobsite:hover:text-neutral-900 jobsite:focus-visible:outline-none jobsite:focus-visible:ring-2 jobsite:focus-visible:ring-neutral-400 jobsite:disabled:pointer-events-none jobsite:disabled:opacity-50"
              >
                <GoogleGlyph />
                Continue with Google
              </button>

              <div className="jobsite:flex jobsite:items-center jobsite:gap-3 jobsite:text-[11px] jobsite:uppercase jobsite:tracking-wide jobsite:text-muted-foreground">
                <span className="jobsite:h-px jobsite:flex-1 jobsite:bg-border" />
                or with email
                <span className="jobsite:h-px jobsite:flex-1 jobsite:bg-border" />
              </div>

              <div className="jobsite:space-y-3">
                <Input
                  type="email"
                  name="email"
                  data-testid="claim-email"
                  autoComplete="email"
                  placeholder="Email address"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  disabled={formState === "submitting"}
                  className="jobsite:h-12 jobsite:text-base"
                />
                <Input
                  type="password"
                  name="password"
                  data-testid="claim-password"
                  autoComplete="new-password"
                  placeholder="Create a password"
                  required
                  minLength={8}
                  value={formData.password}
                  onChange={handleChange}
                  disabled={formState === "submitting"}
                  className="jobsite:h-12 jobsite:text-base"
                />
              </div>

              <Button
                type="submit"
                data-testid="claim-submit"
                disabled={formState === "submitting"}
                className="jobsite:h-12 jobsite:w-full jobsite:font-semibold jobsite:text-base"
                size="lg"
              >
                {formState === "submitting" ? (
                  <span className="jobsite:flex jobsite:items-center jobsite:gap-2">
                    <Loader2 className="jobsite:h-4 jobsite:w-4 jobsite:animate-spin" />
                    Creating account...
                  </span>
                ) : (
                  "Claim My Website"
                )}
              </Button>

              {formState === "error" && (
                <div className="jobsite:flex jobsite:items-start jobsite:gap-2 jobsite:text-sm jobsite:text-destructive">
                  <XCircle className="jobsite:mt-0.5 jobsite:h-4 jobsite:w-4 jobsite:shrink-0" />
                  <span>{errorMessage}</span>
                </div>
              )}

              <p className="jobsite:text-[11px] jobsite:leading-relaxed jobsite:text-muted-foreground">
                By creating an account, you agree to our{" "}
                <a
                  href="https://servicepro.co/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="jobsite:text-foreground jobsite:underline jobsite:underline-offset-[2px]"
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  href="https://servicepro.co/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="jobsite:text-foreground jobsite:underline jobsite:underline-offset-[2px]"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

/** Google's four-color "G" mark. */
function GoogleGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="jobsite:h-5 jobsite:w-5" aria-hidden>
      <path
        fill="#4285F4"
        d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z"
      />
      <path
        fill="#FBBC05"
        d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"
      />
    </svg>
  );
}

export { ClaimBusinessBanner };
